import { defineComponent as _defineComponent } from 'vue'
import { useResizeObserver } from '@@/vuecomposables/resize_observer'
import type { Ref } from 'vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import type { CSSProperties } from 'vue/types/jsx'

/** USAGE GUIDE FOR MULTILINE INPUT
 * *Note: Do NOT put CSS transition for height on this element, if you do the autosizing will NOT work.
 * Method 1: v-model
 * <multiline-input v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * (therefore can do things like `this.value = ""` to clear the field)
 * (also be careful if `value` is remotely updated, it will overwrite the current changes)
 * - On input, `value` data field will changes accordingly
 *
 * Method 2: v-model and v-once
 * <multiline-input v-once v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will changes accordingly
 *
 * Method 3: value prop
 * <multiline-input :value="value" @input="onInput" @enter="onEnter" ref="input"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * - On input, `value` will NOT changes accordingly
 *  - You can use DOM refs to get the value (i.e. `this.$refs.input.$el.value`)
 *  - You can listen to input or enter event to get the value
 *
 * Method 4: value prop and v-once
 * <multiline-input v-once :value="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will NOT changes accordingly (same as above, use DOM ref or events)
 */

// Emitted events: input [newValue], enter, escape, focus, keyup [$event]


export default /*#__PURE__*/_defineComponent({
  __name: 'multiline_input',
  props: {
    value: { default: '' },
    placeholder: { default: '' },
    dir: { default: 'auto' },
    allowNewlines: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    maxLength: { default: undefined },
    darkMode: { type: [Boolean, String], default: 'auto' }
  },
  emits: ["click", "input", "enter", "blur", "keyup", "paste", "arrow-down-on-last-line", "arrow-up-on-first-line", "escape", "focus"],
  setup(__props: any, { expose, emit }: { emit: ({
  (name: 'click', e: MouseEvent): void
  (name: 'input', currentValue: string): void
  (name: 'enter', currentValue: string): void
  (name: 'blur', e: FocusEvent): void
  (name: 'keyup', e: KeyboardEvent): void
  (name: 'paste', e: ClipboardEvent): void
  (name: 'arrow-down-on-last-line'): void
  (name: 'arrow-up-on-first-line'): void
  (name: 'escape'): void
  (name: 'focus'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string, placeholder: string, dir: string, allowNewlines: boolean, readonly: boolean, maxLength: number, darkMode: boolean | 'auto' };





const inputElement = ref<HTMLTextAreaElement>()
const currentValue = ref<string>(props.value)
const height = ref<number | null>(null)

const style = computed<CSSProperties>(() => {
  if (!height.value) return {}
  return { height: `${height.value}px` }
})

function autoResize() {
  height.value = null // Otherwise scrollHeight will just be the previous height
  nextTick(() => {
    if (inputElement.value === undefined) return
    height.value = inputElement.value.scrollHeight
    if (inputElement.value.scrollHeight === 0) {
      setTimeout(() => {
        if (inputElement.value === undefined) return
        height.value = inputElement.value.scrollHeight
      }, 0)
    }
  })
}

watch(currentValue, autoResize)

// v-model receive changes
watch(
  () => props.value,
  () => {
    currentValue.value = props.value
    removeNewlinesIfDisallowed()
  },
)

// v-model emit changes
function onInput(): void {
  removeNewlinesIfDisallowed()
  emit('input', currentValue.value)
}

function onEnter(event: KeyboardEvent): void {
  // In languages like Chinese, Japanese, & Korean, users input characters using an IME.
  // If they are in composing mode (usually indicated by an underline under the word),
  // and they press Enter, we could receive 2 keydown events. Most sources say this is
  // a bug. We can ignore one of the 2 events by checking isComposing or keyCode === 229
  // for legacy browsers.
  // https://github.com/vuejs/vue/issues/10277#issuecomment-873337252
  if (event.isComposing || event.keyCode === 229) return
  if (props.allowNewlines) return
  emit('enter', currentValue.value)
  event.preventDefault()
}

function onArrowDown(): void {
  const textInput = inputElement.value
  if (textInput === undefined) return
  // emit an event when arrow down key is pressed and the cursor is on the last line of the textarea
  // the default behaviour for arrow down on the last line is move to the end of the line
  // we only emit a special event to be handled if the cursor is already at the end of the last line => check selectionStart === value.length
  if (textInput.selectionStart === textInput.value.length) {
    emit('arrow-down-on-last-line')
  }
}

function onArrowUp(): void {
  const textInput = inputElement.value
  if (textInput === undefined) return
  // emit an event when arrow up key is pressed and the cursor is on the first line of the textarea
  // the default behaviour for arrow up on the first line is move to the start of the line
  // we only emit a special event to be handled if the cursor is already at the start of the first line => check selectionStart === 0
  if (textInput.selectionStart === 0) {
    emit('arrow-up-on-first-line')
  }
}

function removeNewlinesIfDisallowed(): void {
  if (props.allowNewlines) return
  const sanitizedValue = currentValue.value.replace(/\n|\r/g, '')
  if (currentValue.value !== sanitizedValue) {
    currentValue.value = sanitizedValue
  }
}

useResizeObserver(inputElement as Ref<HTMLTextAreaElement>, autoResize)

onMounted(() => {
  autoResize()
})

expose({
  focus: () => {
    nextTick(() => inputElement.value?.focus())
  },
  select: () => {
    nextTick(() => inputElement.value?.select())
  },
  blur: () => {
    inputElement.value?.blur()
  },
})

return { __sfc: true,props, emit, inputElement, currentValue, height, style, autoResize, onInput, onEnter, onArrowDown, onArrowUp, removeNewlinesIfDisallowed }
}

})